import { Dispatch, SetStateAction, useEffect } from "react";
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useAppContext } from "../../Contexts";

export type UseQueryWrapperResult<T> = Partial<UseQueryResult<T, Error>> & {
  value: T | undefined;
  loading: boolean;
  refresh: () => Promise<void>;
  lastUpdated: Date | undefined;
};

/**
 * Wrapper around TanStack useQuery that provides built-in error handling
 *
 * Automatically sets errors in AppContext.setError and clears them on success.
 *
 * @example
 * const { value, loading, refresh } = useQueryWrapper({
 *   queryKey: ["baseConfig", extensionRepoPath],
 *   queryFn: async () => {
 *     const response = await fetchBaseJson(extensionRepoPath);
 *     return response;
 *   },
 *   enabled: !!extensionRepoPath,
 *   staleTime: 5 * 60 * 1000,
 * });
 */
const useQueryWrapper = <T,>(
  options: Omit<UseQueryOptions<T, Error, T, QueryKey>, "retry"> & {
    queryKey: QueryKey;
    queryFn: () => Promise<T>;
  },
  setError?: Dispatch<SetStateAction<any>>,
): UseQueryWrapperResult<T> => {
  const { setError: appSetError } = useSafeAppContext();
  const errorSetter = setError || appSetError;

  // Default retry to 2 for robust data fetching
  const queryOptions = {
    retry: 2,
    refetchOnWindowFocus: false,
    ...options,
  };

  const query = useQuery({
    ...queryOptions,
  });

  // Handle error propagation - set on error, clear on success
  useEffect(() => {
    if (query.error) {
      const errorMessage =
        query.error instanceof Error
          ? query.error.message
          : String(query.error);
      errorSetter(errorMessage || "An error occurred");
    } else if (query.status === "success") {
      errorSetter(null);
    }
  }, [query.error, query.status, errorSetter]);
  return {
    ...query,
    value: query.data,
    loading: query.isLoading,
    refresh: async () => {
      await query.refetch();
    },
    lastUpdated: query.dataUpdatedAt
      ? new Date(query.dataUpdatedAt)
      : undefined,
  };
};

/**
 * Safely use AppContext only if available (i.e., within AppProvider).
 * Falls back to a no-op function if context is not available.
 */
const useSafeAppContext = () => {
  try {
    return useAppContext();
  } catch (e) {
    // Not in AppProvider context, return safe defaults
    return { setError: () => {} };
  }
};

export default useQueryWrapper;
